// Credit: the /billing page (both wallets, the trial, buying credit, activity), the small
// credit chip in the chat sidebar, and the action card shown wherever something stops
// because the account is locked or out of AI credit.
//
// Two wallets per account, shared by all of the person's workspaces:
//   hosting — awake time and storage (storage even while asleep)
//   AI      — the agent's model use
// A new account starts with a trial: 30 awake minutes and €2 of AI credit.

import { h, Component } from 'preact';
import { getWallet, walletHistory, startTopup, getOrder } from '../api.js';
import { Icon } from './icons.js';
import './ui.css';

export const eur = (cents) => '€' + (Number(cents || 0) / 100).toFixed(2);
const eurs = (x) => (x == null ? '—' : '€' + Number(x).toFixed(2));
const when = (iso) => { try { return new Date(iso + (/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? '' : 'Z')).toLocaleDateString(undefined, { day: 'numeric', month: 'long' }); } catch (e) { return iso; } };
const mins = (s) => Math.max(0, Math.ceil((s || 0) / 60));

// ─── what a stop means, and what fixes it ─────────────────────────────────────
// Codes come from the portal (402 + code) or from the agent's error text (LiteLLM).
const ACTIONS = {
  trial_ended: { title: 'Your free trial has ended', pocket: 'hosting',
    text: 'Your workspace is asleep with all your files. Buy hosting credit to wake it and keep going.', cta: 'Buy hosting credit' },
  trial_limit: { title: 'The trial includes one workspace', pocket: 'hosting',
    text: 'Buy hosting credit to create more workspaces.', cta: 'Buy hosting credit' },
  hosting_credits: { title: 'Your hosting credit is used up', pocket: 'hosting',
    text: 'Your workspaces are asleep and nothing is being charged. Top up to wake them.', cta: 'Top up hosting' },
  ai_credits: { title: 'Your AI credit is used up', pocket: 'ai',
    text: 'The agent needs AI credit to answer. Top up and send your message again.', cta: 'Top up AI credit' },
  ai_model_locked: { title: 'This model needs AI credit', pocket: 'ai',
    text: 'During the trial only the default model is available. Buy AI credit to use the others.', cta: 'Buy AI credit' },
};

export function classify(text) {
  const low = String(text || '').toLowerCase();
  if (['exceededbudget', 'budget_exceeded', 'over budget', 'budget has been exceeded'].some((m) => low.includes(m))) return 'ai_credits';
  if (['not allowed to access model', 'key not allowed'].some((m) => low.includes(m))) return 'ai_model_locked';
  return null;
}

export function ActionCard({ code, compact }) {
  const a = ACTIONS[code];
  if (!a) return null;
  return h('div', { className: 'action-card' + (compact ? ' compact' : ''), role: 'status' },
    h('span', { className: 'action-icon', 'aria-hidden': 'true' }, a.pocket === 'ai' ? '✦' : '⏻'),
    h('div', { className: 'grow' }, h('b', null, a.title), h('p', null, a.text)),
    h('a', { className: 'btn primary', href: '/billing?buy=' + a.pocket }, a.cta));
}

// ─── the sidebar chip ─────────────────────────────────────────────────────────
export class CreditChip extends Component {
  constructor(p) { super(p); this.state = { w: null }; this.timer = null; }
  componentDidMount() { this.load(); this.timer = setInterval(() => { if (!document.hidden) this.load(); }, 60000); }
  componentWillUnmount() { clearInterval(this.timer); }
  componentDidUpdate(prev) { if (prev.stamp !== this.props.stamp) this.load(); }
  async load() {
    try { const w = await getWallet(); this.setState({ w }); if (this.props.onWallet) this.props.onWallet(w); } catch (e) { /* keep the last */ }
  }
  render(_, { w }) {
    if (!w) return null;
    const t = w.trial || {};
    let label, cls = 'credit-chip';
    if (w.lock) { label = w.lock === 'trial_ended' ? 'Trial ended · buy credit' : 'Out of hosting credit'; cls += ' warn'; }
    else if (t.state === 'none') label = `Free trial · ${t.minutes_total} min`;
    else if (t.state === 'active') label = `Trial · ${mins(t.seconds_left)} min left`;
    else {
      const ai = w.ai && w.ai.remaining_eur != null ? ` · AI ${eurs(w.ai.remaining_eur)}` : '';
      label = `${eur(w.hosting.balance_cents)} hosting${ai}`;
      if (w.hosting.hours_left != null && w.hosting.hours_left < 24) cls += ' warn';
    }
    return h('a', { className: cls, href: '/billing', title: 'Credit and usage' }, h('span', { className: 'coin', 'aria-hidden': 'true' }), label);
  }
}

// ─── the /billing page ────────────────────────────────────────────────────────
function Packs({ packs, pocket, busy, onBuy }) {
  return h('div', { className: 'packs' }, (packs || []).map((c) =>
    h('button', { key: c, type: 'button', className: 'btn pack', disabled: !!busy, onClick: () => onBuy(pocket, c) },
      busy === `${pocket}:${c}` ? '…' : eur(c))));
}

export class BillingPage extends Component {
  constructor(p) {
    super(p);
    this.state = { w: null, hist: null, busy: '', error: '', notice: '', focus: null };
    this.poller = null;
  }

  componentDidMount() {
    const q = new URLSearchParams(location.search);
    this.setState({ focus: q.get('buy') });
    this.load();
    const order = q.get('order');
    if (order) this.watchOrder(order);
  }

  componentWillUnmount() { clearTimeout(this.poller); }

  async load() {
    try {
      const [w, hist] = await Promise.all([getWallet(), walletHistory()]);
      this.setState({ w, hist, error: '' });
    } catch (e) { this.setState({ error: e.message }); }
  }

  // Back from the checkout: the payment may be confirmed a moment later (webhook).
  async watchOrder(id, tries = 0) {
    try {
      const o = await getOrder(id);
      if (o.status === 'PAID') {
        this.setState({ notice: `Thanks — ${eur(o.amount_cents)} of ${o.pocket === 'ai' ? 'AI' : 'hosting'} credit added.` });
        history.replaceState(null, '', '/billing');
        this.load();
        return;
      }
      if (o.status === 'CANCELLED' || o.status === 'FAILED') {
        this.setState({ notice: 'The payment was not completed. Nothing was charged.' });
        history.replaceState(null, '', '/billing');
        return;
      }
      if (tries < 20) { this.setState({ notice: 'Confirming your payment…' }); this.poller = setTimeout(() => this.watchOrder(id, tries + 1), 1500); }
    } catch (e) { this.setState({ error: e.message }); }
  }

  async buy(pocket, cents) {
    this.setState({ busy: `${pocket}:${cents}`, error: '' });
    try {
      const r = await startTopup(pocket, cents);
      window.location.href = r.checkout_url;
    } catch (e) { this.setState({ busy: '', error: e.message }); }
  }

  render(_, s) {
    const w = s.w;
    if (s.error && !w) return h('div', { className: 'bill-page' }, h('p', { className: 'note err' }, s.error));
    if (!w) return h('div', { className: 'bill-page' }, h('div', { className: 'skel' }, h('span', null), h('span', null), h('span', { className: 'short' })));
    const t = w.trial || {}, host = w.hosting || {}, ai = w.ai || {};
    const trialActive = t.state === 'active', trialWaiting = t.state === 'none';
    const payNote = w.payments === 'simulated' ? h('p', { className: 'test-mode' }, h('b', null, 'Test mode. '), 'Online payment isn’t connected yet; purchases use a test checkout and no money moves.')
      : w.payments === 'none' ? h('p', { className: 'note err' }, 'Buying credit isn’t available yet.') : null;
    return h('div', { className: 'bill-page' },
      h('div', { className: 'bill-head' },
        h('h1', null, 'Credit'),
        h('p', { className: 'muted' }, 'Two balances, shared by all your workspaces: hosting pays for awake time and storage, AI pays for the agent. You only pay for what you use.')),
      s.notice ? h('p', { className: 'banner ok', role: 'status' }, s.notice) : null,
      s.error ? h('p', { className: 'note err' }, s.error) : null,
      w.lock ? h(ActionCard, { code: w.lock }) : null,
      w.deletion_at ? h('p', { className: 'banner err', role: 'alert' },
        `Your workspaces and their files will be deleted on ${when(w.deletion_at)} unless you buy hosting credit.`) : null,
      payNote,

      trialWaiting ? h('section', { className: 'bill-card trial' },
        h('div', { className: 'bill-card-head' }, h('h2', null, 'Free trial'), h('span', { className: 'role-badge' }, 'Ready')),
        h('p', null, h('b', null, `${t.minutes_total} minutes of workspace time`), ' and €2 of AI credit, on your first workspace.'),
        h('p', { className: 'muted small' }, 'Start a chat and your workspace is created for you — no card needed.'),
        h('a', { className: 'btn primary', href: '/' }, 'Start your first chat')) : null,

      trialActive ? h('section', { className: 'bill-card trial' },
        h('div', { className: 'bill-card-head' }, h('h2', null, 'Free trial'), h('span', { className: 'role-badge' }, 'Active')),
        h('div', { className: 'meter' }, h('span', { style: { width: `${Math.min(100, 100 * (t.seconds_left || 0) / ((t.minutes_total || 30) * 60))}%` } })),
        h('p', null, h('b', null, `${mins(t.seconds_left)} of ${t.minutes_total} minutes left`), ' of awake workspace time, plus AI credit on the default model.'),
        h('p', { className: 'muted small' }, 'When the minutes run out your workspace sleeps. Buy hosting credit to wake it; unconverted trial workspaces are deleted after 7 days.')) : null,

      h('div', { className: 'bill-grid' },
        h('section', { className: 'bill-card' + (s.focus === 'hosting' ? ' focus' : '') },
          h('div', { className: 'bill-card-head' }, h('h2', null, 'Hosting'), host.depleted_at ? h('span', { className: 'role-badge warn' }, 'Empty') : null),
          h('div', { className: 'big' }, eur(host.balance_cents)),
          h('p', { className: 'muted small' },
            trialActive ? 'Not charged during the trial.'
              : host.burn_cents_per_hour > 0 ? `Using about ${eur(host.burn_cents_per_hour * 24)} a day right now` + (host.hours_left != null ? ` · roughly ${host.hours_left < 48 ? Math.floor(host.hours_left) + ' hours' : Math.floor(host.hours_left / 24) + ' days'} left.` : '.')
              : 'Nothing is running.'),
          h('p', { className: 'muted small' }, `${eurs(w.rates.awake_hour)} per awake hour · ${eurs(w.rates.storage_gb_month)} per GB of storage a month, asleep or awake.`),
          h(Packs, { packs: w.packs, pocket: 'hosting', busy: s.busy, onBuy: (p, c) => this.buy(p, c) })),
        h('section', { className: 'bill-card' + (s.focus === 'ai' ? ' focus' : '') },
          h('div', { className: 'bill-card-head' }, h('h2', null, 'AI'), ai.trial_only ? h('span', { className: 'role-badge' }, 'Default model only') : null),
          h('div', { className: 'big' }, ai.remaining_eur == null ? '—' : eurs(ai.remaining_eur)),
          h('p', { className: 'muted small' }, ai.spent_eur == null ? (ai.credited_eur ? 'Usage is updating…' : 'No AI credit yet.') : `${eurs(ai.spent_eur)} used of ${eurs(ai.credited_eur)} so far.`),
          h('p', { className: 'muted small' }, ai.trial_only ? 'Buying AI credit unlocks every model, including image generation when it arrives.' : 'Every model is available, charged per use.'),
          h(Packs, { packs: w.packs, pocket: 'ai', busy: s.busy, onBuy: (p, c) => this.buy(p, c) }))),

      h('section', { className: 'bill-card' },
        h('h2', null, 'Activity'),
        !(s.hist && s.hist.ledger && s.hist.ledger.length) ? h('p', { className: 'muted small' }, 'Nothing yet.')
          : h('table', { className: 'mini' },
            h('thead', null, h('tr', null, h('th', null, 'When'), h('th', null, 'What'), h('th', null, 'Wallet'), h('th', { className: 'num' }, 'Amount'))),
            h('tbody', null, s.hist.ledger.map((r) => h('tr', { key: r.id },
              h('td', null, when(r.created)),
              h('td', null, r.kind === 'usage' ? (r.note || 'Usage') : r.kind === 'trial' ? 'Trial credit' : r.kind === 'topup' ? 'Top-up' : (r.note || r.kind)),
              h('td', null, r.pocket === 'ai' ? 'AI' : 'Hosting'),
              h('td', { className: 'num' + (r.amount_cents < 0 ? '' : ' plus') }, (r.amount_cents < 0 ? '−' : '+') + eur(Math.abs(r.amount_cents)))))))));
  }
}
